import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { PtLuxonDate } from '../../models/projects/date.models';
import {
  AffectedTransitHelper,
  TransitSaveHelper,
  TransitSearchFilters,
  TransitSummary
} from '../../models/transit/transit.models';
import { AppData, OperationInstanceSaveRequest } from '../../models/app-data/app-data';
import { DailyLogOperationInstance } from '../../models/projects/operation.models';
import { SetupChangeLogRecord } from '../../models/setup/setup.models';
import { DateService } from '../date/date.service';
import { TimeZoneService } from '../time-zone/time-zone.service';
import { TimeZoneChangeEditor } from '../../models/time-zones/time-zone.models';
import { DateParserService } from '../daily-log/daily-log-time/date-parser-service';
import { AppConfig } from '../../app/app.config';
import { OperationService } from '../operations/operation/operation.service';

@Injectable()
export class TransitService {
  config = AppConfig.settings;

  constructor(
    private http: HttpClient,
    private dateService: DateService,
    private timeZoneService: TimeZoneService,
    private dateParserService: DateParserService,
    private operationService: OperationService
  ) {

  }

  // searches for a transit entity that could be elongated - i.e when adding/editing a new rig move, if the operation ends where a transit starts,
  // then the operation would become part of that transit, likewise if the operation starts where an existing transit ends
  async getAffectedTransits(operationStartDate: PtLuxonDate, operationEndDate: PtLuxonDate, operationId?: number): Promise<AffectedTransitHelper> {
    const filters = new TransitSearchFilters();

    // convert to UTC before sending to API
    filters.operationStartDate = operationStartDate && operationStartDate.dateTime
      ? this.dateParserService.removeOffsetFromLuxonDate(operationStartDate.dateTime)
      : null;
    filters.operationEndDate = operationEndDate && operationEndDate.dateTime
      ? this.dateParserService.removeOffsetFromLuxonDate(operationEndDate.dateTime)
      : null;
    filters.operationId = operationId;

    const url = `${this.config.apiUrl}api/transit/getaffectedtransits`;

    const result = await this.http.post<AffectedTransitHelper>(url, filters).toPromise();

    return await this.setAffectedTransitLocalDates(result);
  }

  async updateOrCreateTransitForSave(
    saveRequest: OperationInstanceSaveRequest,
    affectedTransitHelper: AffectedTransitHelper,
    operationInstance: DailyLogOperationInstance,
    createNewTransit: boolean
  ) {
    saveRequest.transitSaveHelper = new TransitSaveHelper();
    saveRequest.transitSaveHelper.transitSummary
      = await this.createOrUpdateTransitSummary(affectedTransitHelper, operationInstance, createNewTransit);
  }

  async createOrUpdateTransitSummary(
    affectedTransitHelper: AffectedTransitHelper,
    operationInstance: DailyLogOperationInstance,
    createNewTransit: boolean
  ): Promise<TransitSummary> {
    operationInstance.currentlyEditing = true; // used to highlight the current op being edited in the preview component

    // set cancelled local date here, or cancelled time won't show on transit summary if the current operation is the one with the cancelled time
    if (operationInstance.cancelledTime) {
      operationInstance.cancelledTimePtDate = new PtLuxonDate(operationInstance.cancelledTime);
      // todo - check needed
      // await operationInstance.cancelledTimePtDate.setLocalDate(this.timeZoneService, false);
    }

    // editing operation that was previously added to a transit - so replace the operation with local one in case user has changed times
    if (affectedTransitHelper.linkedTransit) {
      // no transits detected before or after, so just update existing
      if (!affectedTransitHelper.transitAfterOperation && !affectedTransitHelper.transitBeforeOperation) {
        return this.updateExistingTransit(operationInstance, affectedTransitHelper.linkedTransit);
      }

      affectedTransitHelper.linkedTransit = this.updateExistingTransit(operationInstance, affectedTransitHelper.linkedTransit);

      return this.createMergedPreviewOperationInstances(affectedTransitHelper);
    }

    // adding an operation that doesn't connect with any other existing transit
    if (!affectedTransitHelper.transitBeforeOperation && !affectedTransitHelper.transitAfterOperation && createNewTransit) {
      return this.createNewTransit(operationInstance);
    }

    // adding operation that connects to the end of a previous transit (op start time is equal or less than transit start)
    if (affectedTransitHelper.transitBeforeOperation && !affectedTransitHelper.transitAfterOperation) {
      return this.appendOperationToTransit(operationInstance, affectedTransitHelper.transitBeforeOperation);
    }

    // adding operation that connects to the start of a previous transit (op end time is equal or greater than transit start)
    if (!affectedTransitHelper.transitBeforeOperation && affectedTransitHelper.transitAfterOperation) {
      return this.prependOperationToTransit(operationInstance, affectedTransitHelper.transitAfterOperation);
    }
  }

  updateExistingTransit(
    operationInstance: DailyLogOperationInstance,
    transit: TransitSummary
  ): TransitSummary {
    const index = transit.operationInstances.findIndex(e => e.id === operationInstance.id);

    if (index !== null && index !== -1) {
      transit.operationInstances.splice(index, 1, operationInstance);
    } else {
      transit.operationInstances.push(operationInstance);
    }

    transit = this.verifyTransitPreviewIntegrity(transit);

    return transit;
  }


  createNewTransit(operationInstance: DailyLogOperationInstance): TransitSummary {
    let transitSummary = new TransitSummary();

    transitSummary.operationInstances = [operationInstance];

    transitSummary = this.verifyTransitPreviewIntegrity(transitSummary);

    return transitSummary;
  }

  appendOperationToTransit(operationInstance: DailyLogOperationInstance, transit: TransitSummary): TransitSummary {
    const index = transit.operationInstances.findIndex(e => e.id === operationInstance.id);

    if (index !== null && index !== -1) {
      transit.operationInstances.splice(index, 1, operationInstance);
    } else {
      transit.operationInstances.push(operationInstance);
    }

    transit.operationInstances = transit.operationInstances.sort((function (a, b) {
      return a.startTime.dateTime.toMillis() - b.startTime.dateTime.toMillis();
    }));

    return transit;
  }

  prependOperationToTransit(operationInstance: DailyLogOperationInstance, transit: TransitSummary): TransitSummary {
    transit.operationInstances.unshift(operationInstance);

    return transit;
  }

  // these mergedPreview operation instances will be shown to the user instead of the actual instances on the transit
  // we don't want to overwrite the actual instances because these get sent to the api on save, and sending the mergePreviewInstances
  // instead messes up the transit merge logic on the API
  createMergedPreviewOperationInstances(affectedTransitHelper: AffectedTransitHelper): TransitSummary {
    if (affectedTransitHelper.transitBeforeOperation) {
      affectedTransitHelper.linkedTransit.mergePreviewOperationInstances = affectedTransitHelper.linkedTransit.operationInstances
        .concat(affectedTransitHelper.transitBeforeOperation.operationInstances);
    }

    if (affectedTransitHelper.transitAfterOperation) {
      affectedTransitHelper.linkedTransit.mergePreviewOperationInstances = affectedTransitHelper.linkedTransit.operationInstances
        .concat(affectedTransitHelper.transitAfterOperation.operationInstances);
    }

    return affectedTransitHelper.linkedTransit;
  }

  private async setAffectedTransitLocalDates(affectedTransitHelper: AffectedTransitHelper): Promise<AffectedTransitHelper> {
    if (affectedTransitHelper.linkedTransit && affectedTransitHelper.linkedTransit.operationInstances) {
      for (let i = 0; i < affectedTransitHelper.linkedTransit.operationInstances.length; i++) {
        affectedTransitHelper.linkedTransit = await this.setStartEndLocalDatesForTransit(affectedTransitHelper.linkedTransit);
        affectedTransitHelper.linkedTransit.operationInstances[i] =
          await this.setOperationLocalDatesForTransit(affectedTransitHelper.linkedTransit.operationInstances[i]);
      }
    }

    if (affectedTransitHelper.transitBeforeOperation && affectedTransitHelper.transitBeforeOperation.operationInstances) {
      for (let i = 0; i < affectedTransitHelper.transitBeforeOperation.operationInstances.length; i++) {
        affectedTransitHelper.transitBeforeOperation = await this.setStartEndLocalDatesForTransit(affectedTransitHelper.transitBeforeOperation);
        affectedTransitHelper.transitBeforeOperation.operationInstances[i] =
          await this.setOperationLocalDatesForTransit(affectedTransitHelper.transitBeforeOperation.operationInstances[i]);
      }
    }

    if (affectedTransitHelper.transitAfterOperation && affectedTransitHelper.transitAfterOperation.operationInstances) {
      for (let i = 0; i < affectedTransitHelper.transitAfterOperation.operationInstances.length; i++) {
        affectedTransitHelper.transitAfterOperation = await this.setStartEndLocalDatesForTransit(affectedTransitHelper.transitAfterOperation);
        affectedTransitHelper.transitAfterOperation.operationInstances[i] =
          await this.setOperationLocalDatesForTransit(affectedTransitHelper.transitAfterOperation.operationInstances[i]);
      }
    }

    return affectedTransitHelper;
  }

  private verifyTransitPreviewIntegrity(transit: TransitSummary) {
    transit.operationInstances = transit.operationInstances.sort(function (a, b) {
      // sort in order of startTime
      return a.startTime.dateTime.toMillis() - b.startTime.dateTime.toMillis();
    });
    if (transit.operationInstances && transit.operationInstances.length > 1) {
      let operationsToRemove = [];
      let gapFound = false;

      for (let i = 1; i < transit.operationInstances.length; i++) {
        if (transit.operationInstances[i].startTime.dateTime > transit.operationInstances[i - 1].endTime.dateTime) {

          // can't just rely on the above if to find gaps, if a concurrent op is logged at [i-1] it would pass the if statement, but
          // [i-2] could still have an end that runs until [i] start time, so check here for other operations in the array that
          // start before [i] but end after i
          if (transit.operationInstances
            .filter(e => e.startTime.dateTime < transit.operationInstances[i].startTime.dateTime
              && ((e.cancelledTime == null && (e.endTime == null || e.endTime.dateTime == null || e.endTime.dateTime >= transit.operationInstances[i].startTime.dateTime))
                || (e.cancelledTime >= transit.operationInstances[i].startTime.dateTime)))
            .length > 0) {
            continue;
          }

          gapFound = true;
          const gapStartTime = transit.operationInstances[i - 1].endTime.dateTime;

          // gap found - get operations from transit if they start after the gap
          operationsToRemove = transit.operationInstances.filter(operation => operation.startTime.dateTime > gapStartTime);

          break;
        }
      }

      if (gapFound) {
        transit.integrityBroken = true;
        operationsToRemove.forEach(operation => {
          operation.removingFromTransit = true;
        });
      }

    }

    return transit;
  }

  private async setStartEndLocalDatesForTransit(transitSummary: TransitSummary): Promise<TransitSummary> {
    if (transitSummary.startDate) {
      transitSummary.startDate = await this.dateService.setPtLuxonDateFromApi(transitSummary.startDate);
    }

    if (transitSummary.endDate) {
      transitSummary.endDate = await this.dateService.setPtLuxonDateFromApi(transitSummary.endDate);
    }

    return transitSummary;
  }

  private async setOperationLocalDatesForTransit(operationInstance: DailyLogOperationInstance): Promise<DailyLogOperationInstance> {
    operationInstance = await this.operationService.deserializeInstanceTimes(operationInstance);

    return operationInstance;
  }
}



// WEBPACK FOOTER //
// ./src/services/transit/transit.service.ts